import { DealDto, DealOfferOption } from '@app/model/deal.model';
import {
    DealInfoDto,
    DealInfoWithAccomOptionDto,
    DealInfoWithMultiSelectOptionDto,
    DealInfoWithSingleSelectOptionDto,
    DealOptionIdDto,
    ProductBookingData,
} from './booking-config.model';

export const isBookingDataValid = (
    productBookingData: ProductBookingData
): boolean => {
    if (!productBookingData) return false;
    if (allSelectedBookingOptionQuantitiesAreZero(productBookingData))
        return false;

    return true;
};

const allSelectedBookingOptionQuantitiesAreZero = (
    productBookingData: ProductBookingData
): boolean => {
    if (!productBookingData.selectedDealOptionQuantities) return false;
    const values = Array.from(
        productBookingData.selectedDealOptionQuantities.values()
    );

    const allQuantitiesAreZero = values.every(
        (quantity) => !quantity || quantity === 0
    );
    return allQuantitiesAreZero;
};

export const createDealInfo = (
    productData: ProductBookingData,
    productDetails: DealDto
): string => {
    if (
        productData.selectedAccomOptions &&
        productData.selectedAccomOptions.length > 0
    ) {
        return createDealInfoWithAccomOption(productData, productDetails);
    } else if (
        productData.selectedDealOptions &&
        productData.selectedDealOptions.length > 0
    ) {
        return createSingleSelectDealInfoDto(productData, productDetails);
    } else if (
        productData.selectedDealOptionQuantities &&
        productData.selectedDealOptionQuantities.size > 0
    ) {
        return createMultiSelectDealInfoDto(productData, productDetails);
    } else {
        return '';
    }
};

const createSingleSelectDealInfoDto = (
    productData: ProductBookingData,
    productDetails: DealDto
): string => {
    const baseDealInfoDto = createBaseDealInfoDto(productDetails);
    const selectedDealOption = productData.selectedDealOptions[0];
    if (!selectedDealOption) {
        // Sentry.captureException(new Error('No deal option selected'));
        return '';
    }

    const otherDealInfoDto: DealInfoWithSingleSelectOptionDto = {
        ...baseDealInfoDto,
        dealOptionIds: [
            {
                dealOptionId: selectedDealOption.dealOptionId,
                quantity: 1,
                dealOfferName: selectedDealOption.offerTitle,
                dealOptionName: selectedDealOption.title,
                dealOptionSubtitle: selectedDealOption.subTitle,
                subtotal: selectedDealOption.sellingPrice,
            },
        ],
    };
    return JSON.stringify(otherDealInfoDto);
};

const createDealInfoWithAccomOption = (
    productData: ProductBookingData,
    productDetails: DealDto
): string => {
    const baseDealInfoDto = createBaseDealInfoDto(productDetails);
    const [selectedAccomOption] = productData.selectedAccomOptions;

    const dealInfoWithAccomOptionDto: DealInfoWithAccomOptionDto = {
        ...baseDealInfoDto,
        totalNights: productData.totalNights,
        roomId: selectedAccomOption.roomId,
        roomCategory: selectedAccomOption.roomName,
        mealPlan: selectedAccomOption.mealPlanType,
        mealPlanId: selectedAccomOption.mealPlanId,
        occupancyId: selectedAccomOption.occupancyId,
        occupancy: selectedAccomOption.occupancyStringWithAges,
    };
    return JSON.stringify(dealInfoWithAccomOptionDto);
};

const createMultiSelectDealInfoDto = (
    productData: ProductBookingData,
    productDetails: DealDto
): string => {
    const baseDealInfoDto = createBaseDealInfoDto(productDetails);
    const quantitiesDealInfoDto: DealInfoWithMultiSelectOptionDto = {
        ...baseDealInfoDto,
        dealOptionIds: createDealOptionIdObject(
            productData.selectedDealOptionQuantities
        ),
    };
    return JSON.stringify(quantitiesDealInfoDto);
};

const createDealOptionIdObject = (
    optionQuantities: Map<DealOfferOption, number>
): DealOptionIdDto[] => {
    return Array.from(optionQuantities.entries())
        .map(([option, quantity]) => {
            return {
                dealOptionId: option.dealOptionId,
                quantity,
                dealOfferName: option.offerTitle,
                dealOptionName: option.title,
                dealOptionSubtitle: option.subTitle,
                subtotal: option.sellingPrice * quantity,
            };
        })
        .filter((dealOption) => dealOption.quantity > 0);
};

const createBaseDealInfoDto = (productDetails: DealDto): DealInfoDto => {
    return {
        dealConditions: JSON.stringify(
            productDetails.staticContentV2.conditions
        ),
        packageTypeId: productDetails.packageTypeId,
        redeemLimit: productDetails.redeemLimit,
    };
};
